import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { WorkspaceOnboardingProgress } from "@/features/home/onboarding/onboarding-progress";
// reason: NotificationsBell is a feature widget composed into the app shell -
// the only seam where the topbar chrome can host the per-user unread badge.
// Matches the pre-existing layering exception for auth.context / sidebar-footer.
// architecture#A6 with PR8 justification.
import { NotificationsBell } from "@/features/notifications/components/notifications-bell";
// reason: CheckInCta is a feature widget composed into the app shell -
// the daily check-in entry point (the mood orb) lives in the chrome so the
// habit loop is reachable from every page, same layering exception as
// NotificationsBell. architecture#A6 with PR8 justification.
import { CheckInCta } from "@/features/pulse-surveys/components/check-in-cta/check-in-cta";
import { useAuth } from "@/shared/auth/auth.context";
import { MobileNav } from "@/shared/components/layout/mobile-nav";
import { usePageBreadcrumbState } from "@/shared/components/layout/page-breadcrumb";
import { TopbarBreadcrumb } from "@/shared/components/layout/topbar-breadcrumb";
import { ThemeToggle } from "@/shared/components/theme-toggle";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { cn } from "@/shared/lib/cn";

export function Topbar({ copilotTrigger }: { copilotTrigger?: ReactNode }) {
  const auth = useAuth();
  const { location } = useRouterState();
  const { workspace, isResolved } = useActiveWorkspace();
  const { trailing } = usePageBreadcrumbState();
  const isWorkspacePending = auth.isLoading && !isResolved;
  const showWorkspaceActions = !isWorkspacePending && isResolved;

  if (isWorkspacePending) {
    return (
      <header
        className={cn(
          "flex items-center justify-between gap-4",
          "gap-2 px-4 py-3 sm:px-6 md:px-8",
          "backdrop-blur-md backdrop-saturate-150",
          "bg-[color-mix(in_oklab,var(--surface-base)_82%,transparent)]",
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Skeleton className="h-4 w-32 rounded-full" />
          <Skeleton className="h-4 w-20 rounded-full" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-md" />
        </div>
      </header>
    );
  }

  return (
    <header
      className={cn(
        "flex items-center justify-between gap-4",
        "gap-2 px-4 py-3 sm:px-6 md:px-8",
        "backdrop-blur-md backdrop-saturate-150",
        "bg-[color-mix(in_oklab,var(--surface-base)_82%,transparent)]",
      )}
    >
      <div className="flex min-w-0 items-center gap-1">
        <MobileNav />
        <TopbarBreadcrumb
          pathname={location.pathname}
          workspaceName={workspace.name}
          workspaceSlug={workspace.slug}
          isResolved={isResolved}
          trailing={trailing}
        />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {copilotTrigger}
        <PageActions pathname={location.pathname} />
        {showWorkspaceActions ? (
          <>
            <WorkspaceOnboardingProgress workspaceId={workspace.id} />
            <CheckInCta workspaceId={workspace.id} mount="topbar" />
            <NotificationsBell workspaceId={workspace.id} />
          </>
        ) : null}
        <ThemeToggle />
      </div>
    </header>
  );
}

/**
 * Page-specific actions in the topbar. Pathname-switch for now, when we hit a third
 * page-specific action, lift to a `useTopbarActions` context (rule A7).
 */
function PageActions({ pathname: _pathname }: { pathname: string }) {
  return null;
}
